// Mini daily puzzle (/mini, Jack 2026-10-26): device storage, streaks, share
// text and loading the rules engine, Blue's bot and the dictionary.
//
// Everything a signed-out player has lives in localStorage under STORE_KEY,
// read and written in try/catch so the page still works without it. Results
// are kept by puzzle number: { won, finished, moves, par, history }, where
// history is the list of moves played (Blue's replies are worked out again).

export const STORE_KEY = "jangle:mini";
// One shot (Jack, 2026-10-26 07:11Z): no tries, but the game stops here.
export const MAX_MOVES = 6;
// Same ?v= as jangle.html loads words.js with, so it comes from the cache.
// bot-lab/mini-check.js fails if the two drift apart.
export const WORDS_V = "19cbe8fa7e";
// Bump when jangle-engine.js changes in a way /mini needs.
export const ENGINE_V = "20261027-cuts";

export function readStore() {
  try {
    const s = JSON.parse(localStorage.getItem(STORE_KEY) || "null");
    if (s && typeof s === "object") return { results: {}, ...s };
  } catch { /* private mode, blocked storage or bad JSON */ }
  return { results: {} };
}

export function writeStore(store) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(store)); } catch { /* ignore */ }
}

// The player's own calendar date, YYYY-MM-DD (like Wordle, not UTC).
export function localDate(d = new Date()) {
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function msToMidnight(d = new Date()) {
  const next = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
  return next - d;
}

// Played, wins, win %, the current run of solved days ending today (or
// yesterday while today's is still open) and the best run.
export function stats(results, today) {
  const nums = Object.keys(results).map(Number);
  const done = nums.filter((n) => results[n].finished);
  const wins = done.filter((n) => results[n].won);
  const won = (n) => !!results[n]?.won;
  let streak = 0;
  let n = won(today) ? today : results[today]?.finished ? 0 : today - 1;
  while (n > 0 && won(n)) { streak++; n--; }
  let best = 0, run = 0;
  const last = Math.max(today, ...nums, 0);
  for (let k = 1; k <= last; k++) { run = won(k) ? run + 1 : 0; best = Math.max(best, run); }
  return {
    played: done.length,
    wins: wins.length,
    winPct: done.length ? Math.round((100 * wins.length) / done.length) : 0,
    streak,
    best,
    underPar: wins.filter((k) => results[k].moves && results[k].moves <= results[k].par).length,
  };
}

// What the account keeps of each day (backend/mini.py DayResult).
export function accountResults(results) {
  const out = {};
  for (const [k, r] of Object.entries(results)) {
    if (r && typeof r.moves === "number") out[k] = { won: !!r.won, finished: !!r.finished, moves: r.moves || 0, par: r.par || 0 };
  }
  return out;
}

// What "Share result" sends: no letters, so nothing is spoiled.
export function shareText(n, r) {
  const marks = [];
  for (let k = 0; k < r.moves; k++) marks.push(r.won && k === r.moves - 1 ? "🚩" : "🟪");
  const line = r.won
    ? `Won in ${r.moves} move${r.moves === 1 ? "" : "s"} (par ${r.par})`
    : `Blue won · par ${r.par}`;
  return `Jangle Mini #${n} ${r.won ? "🚩" : "🛡️"}\n${line}\n${marks.join("")}${r.won ? "" : "💥"}\nplayjangle.com/mini`;
}

// The rules engine and the dictionary, as classic scripts (once per page load).
let loading = null;
function addScript(src) {
  return new Promise((resolve, reject) => {
    const el = document.createElement("script");
    el.src = src;
    el.async = true;
    el.onload = resolve;
    el.onerror = () => reject(new Error(`Couldn't load ${src}`));
    document.head.appendChild(el);
  });
}
export function loadEngine() {
  if (!loading) {
    loading = Promise.all([
      addScript(`/game/bot/jangle-engine.js?v=${ENGINE_V}`).then(() => Promise.all([
        addScript(`/game/bot/jangle-bot.js?v=${ENGINE_V}`),
        addScript(`/game/bot/mini-game.js?v=${ENGINE_V}`),
      ])),
      addScript(`/game/words.js?v=${WORDS_V}`),
      fetch(`/game/bot/word-rank.txt?v=${ENGINE_V}`).then((r) => {
        if (!r.ok) throw new Error("Couldn't load Blue's words");
        return r.text();
      }),
    ]).then(([, , rankText]) => {
      const E = window.JangleEngine;
      // words.js declares `const WORDS`, a global binding but not a window property.
      // eslint-disable-next-line no-new-func
      E.setDictionary(new Function("return WORDS")());
      return { E, B: window.JangleBot, G: window.JangleMini, rank: rankText.trim().split(/\s+/) };
    });
    loading.catch(() => { loading = null; });
  }
  return loading;
}
